import React, { useState, useEffect, useRef } from 'react';
import { 
  Clipboard, 
  Download, 
  Instagram, 
  Youtube, 
  AlertCircle, 
  Loader2,
  X,
  Check,
  Zap,
  Smartphone,
  HelpCircle
} from 'lucide-react';

// YOUR RENDER BACKEND URL
const BACKEND_URL = "https://royal-backend-y1zh.onrender.com";

function App() {
  const [url, setUrl] = useState('');
  const [clipboardUrl, setClipboardUrl] = useState(null);
  const [showClipboardPrompt, setShowClipboardPrompt] = useState(false);
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [feedback, setFeedback] = useState('');
  const inputRef = useRef(null);

  // --- LINK DETECTION ---
  const isSupportedUrl = (text) => {
    if (!text) return false;
    const instaRegex = /instagram\.com\/(reel|p)\/[\w-]+\/?/i;
    const ytShortsRegex = /(youtube\.com\/shorts\/|youtu\.be\/)[\w-]+\/?/i;
    return instaRegex.test(text) || ytShortsRegex.test(text);
  };

  const getPlatformName = (text) => {
    if (/instagram\.com/i.test(text)) return 'Instagram Reel';
    if (/youtube\.com|youtu\.be/i.test(text)) return 'YouTube Short';
    return 'Link';
  };

  useEffect(() => {
    const checkClipboard = async () => {
      if (navigator.clipboard && document.visibilityState === 'visible') {
        try {
          const text = await navigator.clipboard.readText();
          if (isSupportedUrl(text) && text !== clipboardUrl) {
            setClipboardUrl(text);
            setShowClipboardPrompt(true);
          }
        } catch (err) {
          console.log('Clipboard permission denied');
        }
      }
    };

    checkClipboard();
    const handleVisibilityChange = () => { if (document.visibilityState === 'visible') checkClipboard(); };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    if (inputRef.current) inputRef.current.focus();
    
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [clipboardUrl]);

  const handleClipboardAccept = () => {
    if (isSupportedUrl(clipboardUrl)) {
      setUrl(clipboardUrl);
      setShowClipboardPrompt(false);
      setError('');
      processUrl(clipboardUrl);
    } else {
      setError('Copied link is not a valid Instagram or YouTube URL.');
      setShowClipboardPrompt(false);
    }
  };

  const handleInputChange = (e) => {
    setUrl(e.target.value);
    setError('');
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (isSupportedUrl(url)) {
      processUrl(url);
    } else {
      setError('Please enter a valid Instagram Reel or YouTube Short URL.');
    }
  };

  const processUrl = async (inputUrl) => {
    setIsProcessing(true);
    setError('');
    setFeedback('');
    try {
      const response = await fetch(`${BACKEND_URL}/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: inputUrl }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setFeedback('Download Started!');
        // Force download
        const proxyUrl = `${BACKEND_URL}/stream_file?url=${encodeURIComponent(data.video_url)}&filename=${data.filename}`;
        window.location.href = proxyUrl;
        setTimeout(() => setFeedback(''), 3000);
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.detail || 'Server Error';
        
        // Friendly error messages
        if (errorMessage.includes('Sign in')) {
           setError("Server is updating cookies. Please try again in 5 minutes.");
        } else {
           setError(`Error: ${errorMessage}`);
        }
      }
    } catch (err) {
      setError('Connection failed. Please try again later.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-slate-200 font-sans relative overflow-hidden selection:bg-purple-500/30">
      
      {/* Background Decor */}
      <div className="fixed top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-purple-900/20 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-amber-900/10 rounded-full blur-[120px]"></div>
      </div>

      <div className="container mx-auto px-4 py-12 flex flex-col items-center max-w-4xl">
        
        {/* Header */}
        <header className="text-center mb-12 animate-fade-in-down">
          <div className="inline-flex items-center justify-center p-3 bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 rounded-2xl mb-4 shadow-xl shadow-purple-900/10">
            <Instagram className="w-6 h-6 text-purple-400 mr-2" />
            <span className="text-slate-400 mx-2">+</span>
            <Youtube className="w-6 h-6 text-red-500 mr-2" />
            <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-amber-400">
              InstaGrabber
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-4">
            Download Reels & <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">Shorts</span>
          </h1>
          <p className="text-slate-400 text-lg max-w-md mx-auto">
            Paste any Instagram Reel or YouTube Short link below.
            <br/>Instant HD download. No watermark.
          </p>
        </header>

        {/* Clipboard Toast */}
        {showClipboardPrompt && (
          <div className="w-full max-w-2xl mb-8 animate-fade-in-up">
            <div className="bg-slate-900/80 backdrop-blur-md border border-amber-500/30 rounded-xl p-4 flex items-center justify-between shadow-lg shadow-amber-900/10 ring-1 ring-amber-500/20">
              <div className="flex items-center overflow-hidden">
                <div className="p-2 bg-amber-500/10 rounded-lg mr-3">
                  <Clipboard className="w-5 h-5 text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-amber-200">{getPlatformName(clipboardUrl)} detected!</p>
                  <p className="text-xs text-slate-400 truncate w-48 md:w-64">{clipboardUrl}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-3">
                <button onClick={handleClipboardAccept} className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold rounded-lg transition-colors">Paste & Go</button>
                <button onClick={() => setShowClipboardPrompt(false)} className="p-1.5 text-slate-400 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        )}

        {/* Main Input Card */}
        <div className="w-full max-w-2xl bg-slate-900/50 backdrop-blur-xl rounded-3xl border border-white/5 shadow-2xl p-2 md:p-8 mb-16 relative z-10">
          <form onSubmit={handleFormSubmit} className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-red-600 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-1000"></div>
            <div className="relative flex items-center bg-slate-950 rounded-2xl border border-slate-800 focus-within:border-purple-500/50 transition-colors shadow-inner">
              <input
                ref={inputRef}
                type="text"
                value={url}
                onChange={handleInputChange}
                placeholder="Paste Instagram or YouTube link..."
                className="w-full bg-transparent text-white placeholder-slate-500 px-6 py-5 rounded-2xl focus:outline-none text-lg"
                disabled={isProcessing}
              />
              <button
                type="submit"
                disabled={!url.trim() || isProcessing}
                className={`absolute right-2 top-2 bottom-2 px-6 rounded-xl font-medium flex items-center justify-center transition-all duration-300 ${
                  url.trim() && !isProcessing
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white shadow-lg shadow-purple-900/20'
                    : 'bg-slate-800 text-slate-500 cursor-not-allowed'
                }`}
              >
                {isProcessing ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="flex items-center">Download <Download className="w-4 h-4 ml-2" /></span>}
              </button>
            </div>
          </form>
          {error && (
            <div className="mt-6 flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-200 text-sm animate-shake">
              <AlertCircle className="w-5 h-5 flex-shrink-0 text-red-400" />
              <p>{error}</p>
            </div>
          )}
        </div>

        {/* Features Grid */}
        <section className="grid md:grid-cols-3 gap-6 w-full mb-20 max-w-6xl mx-auto px-4">
            <div className="bg-slate-900/40 p-6 rounded-2xl border border-white/5 hover:border-purple-500/30 transition-colors">
                <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4 text-purple-400"><Zap className="w-6 h-6"/></div>
                <h3 className="text-xl font-bold text-white mb-2">Lightning Fast</h3>
                <p className="text-slate-400 text-sm">Our advanced servers process your video in milliseconds. No waiting, no queues.</p>
            </div>
            <div className="bg-slate-900/40 p-6 rounded-2xl border border-white/5 hover:border-red-500/30 transition-colors">
                <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center mb-4 text-red-400"><Youtube className="w-6 h-6"/></div>
                <h3 className="text-xl font-bold text-white mb-2">Reels & Shorts</h3>
                <p className="text-slate-400 text-sm">One tool for everything. Download from both Instagram and YouTube instantly.</p>
            </div>
            <div className="bg-slate-900/40 p-6 rounded-2xl border border-white/5 hover:border-pink-500/30 transition-colors">
                <div className="w-12 h-12 bg-pink-500/10 rounded-lg flex items-center justify-center mb-4 text-pink-400"><Smartphone className="w-6 h-6"/></div>
                <h3 className="text-xl font-bold text-white mb-2">All Devices</h3>
                <p className="text-slate-400 text-sm">Works perfectly on iPhone, Android, Windows, and Mac.</p>
            </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full max-w-3xl mx-auto mb-16 px-4">
          <div className="flex items-center gap-3 mb-8">
            <HelpCircle className="w-6 h-6 text-purple-400" />
            <h2 className="text-2xl font-bold text-white">Frequently Asked Questions</h2>
          </div>
          
          <div className="space-y-4">
            <div className="bg-slate-900/30 border border-white/5 rounded-xl p-5 hover:border-purple-500/20 transition-colors">
              <h3 className="font-semibold text-white mb-2">Is this tool free?</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Yes! InstaGrabber is 100% free to use. You don't need to register, sign up, or pay a subscription fee.</p>
            </div>

            <div className="bg-slate-900/30 border border-white/5 rounded-xl p-5 hover:border-purple-500/20 transition-colors">
              <h3 className="font-semibold text-white mb-2">Does it download in HD?</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Absolutely. We automatically fetch the highest resolution available (up to 1080p or 4K) from Instagram and YouTube.</p>
            </div>

            <div className="bg-slate-900/30 border border-white/5 rounded-xl p-5 hover:border-purple-500/20 transition-colors">
              <h3 className="font-semibold text-white mb-2">Where are videos saved?</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Videos are saved to your device's default "Downloads" folder. On iPhone, check the "Files" app; on Android, check your Gallery.</p>
            </div>

            <div className="bg-slate-900/30 border border-white/5 rounded-xl p-5 hover:border-purple-500/20 transition-colors">
              <h3 className="font-semibold text-white mb-2">Is it safe to use?</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Yes. We do not store your data, history, or the videos you download. The connection is secure and private.</p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="text-center text-slate-600 text-xs pb-8 border-t border-white/5 pt-8 w-full">
          <p>
            © {new Date().getFullYear()} InstaGrabber. Not affiliated with Instagram/Meta or YouTube.
            <span className="mx-2">|</span>
            <a href="#" className="hover:text-purple-400 transition-colors">Privacy Policy</a>
            <span className="mx-2">|</span>
            <a href="#" className="hover:text-purple-400 transition-colors">Terms of Service</a>
          </p>
        </footer>
      </div>

      {feedback && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-emerald-500 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-2 animate-bounce-short z-50">
          <Check className="w-4 h-4" />
          <span className="font-medium">{feedback}</span>
        </div>
      )}
    </div>
  );
}

export default App;